// #5 Page header (DC-16): the page title (his screen locator) as the h1 with its status pills, a meta line of facts
// (ids, level, people) separated by "·", and the page's actions on the right. One line at 1440 px: usePageHeadFit is
// the alt lane's fitHeaders as a hook. Each pass measures the header and steps it: the actions go compact (tight),
// then the title steps down to 22 px (tighter), until the title row holds one line; a meta line that would wrap beside
// the actions runs full width under them (wide); and when the actions still run past the edge, or the title still
// wraps, the header stacks (title, meta, then the actions wrapping under them). It refits when its width changes and
// when the fonts load, and every pass registers with the capture settle signal, so a frame never photographs a header
// mid-fit. Loading keeps the title and draws the meta as skeleton blocks.

import { Fragment, useRef, useState, type DependencyList, type ReactNode, type RefObject } from "react";
import { Skeleton } from "../core";
import { cx } from "../lib/cx";
import { useCaptureSettle } from "../lib/McRoot";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";

/** The fit classes, in the order the passes add them. */
export const PAGE_HEAD_FIT = ["mc-tight", "mc-tighter", "mc-wide", "mc-stack"] as const;
export type PageHeadFitClass = (typeof PAGE_HEAD_FIT)[number];

/** How many visual lines the children of an element take (alt lib.js `lines`). */
function lines(element: Element): number {
  const boxes = Array.from(element.children)
    .map((child) => child.getBoundingClientRect())
    .filter((box) => box.width > 0)
    .sort((a, b) => a.top - b.top);
  let count = 0;
  let bottom = -1;
  for (const box of boxes) {
    if (box.top >= bottom - 1) {
      count += 1;
      bottom = box.bottom;
    } else {
      bottom = Math.max(bottom, box.bottom);
    }
  }
  return count;
}

/** One fit pass over a header element: returns the classes it needs (the element's classList is stepped meanwhile). */
export function fitPageHead(head: HTMLElement): PageHeadFitClass[] {
  head.classList.remove(...PAGE_HEAD_FIT);
  const acts = head.querySelector(":scope > .mc-ph-acts");
  if (!acts) return [];
  const out: PageHeadFitClass[] = [];
  const add = (name: PageHeadFitClass) => {
    head.classList.add(name);
    out.push(name);
  };
  const title = head.querySelector(".mc-ph-pt");
  if (title && lines(title) > 1) add("mc-tight");
  if (title && lines(title) > 1) add("mc-tighter");
  const meta = head.querySelector(":scope > .mc-ph-meta");
  if (meta && lines(meta) > 1) add("mc-wide");
  if (acts.getBoundingClientRect().right > head.getBoundingClientRect().right + 1) add("mc-stack");
  const h1 = head.querySelector(".mc-ph-h");
  if (h1 && !out.includes("mc-stack")) {
    const style = getComputedStyle(h1);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.25;
    if (h1.getBoundingClientRect().height > lineHeight * 1.5) add("mc-stack");
  }
  return out;
}

/**
 * Fits a page header to one line (DC-16) and returns its fit classes. Runs a pass on mount, when the header's width
 * changes, when the fonts are ready and when `deps` change; each pass is registered with useCaptureSettle().
 */
export function usePageHeadFit(ref: RefObject<HTMLElement | null>, deps: DependencyList = []): string {
  const settle = useCaptureSettle();
  const [fit, setFit] = useState("");
  useIsoLayoutEffect(() => {
    const head = ref.current;
    if (!head) return;
    let live = true;
    let width = -1;
    let frame = 0;
    const pass = () => {
      if (!live) return;
      width = head.getBoundingClientRect().width;
      setFit(fitPageHead(head).join(" "));
    };
    // The pass, and a settle item that resolves on the next frame (after React has committed the classes).
    const run = () => {
      settle.pending(new Promise<void>((resolve) => {
        pass();
        requestAnimationFrame(() => resolve());
      }));
    };
    run();
    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(() => {
        if (Math.abs(head.getBoundingClientRect().width - width) < 0.5) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(run);
      })
      : null;
    observer?.observe(head);
    const fonts = typeof document !== "undefined" ? document.fonts?.ready : undefined;
    if (fonts) settle.pending(fonts.then(() => { if (live) run(); }));
    return () => {
      live = false;
      observer?.disconnect();
      cancelAnimationFrame(frame);
    };
    // The caller's deps (title, pills, actions) re-run the fit; ref and settle are stable.
  }, [ref, settle, ...deps]);
  return fit;
}

export interface PageHeaderProps {
  /** The page title: his screen locator (*Intake Part 2*, *Kickoff & Packets*, *Offer package — {candidate}*). */
  title: string;
  /** Status pills beside the title. */
  pills?: ReactNode | undefined;
  /** The facts under the title, separated by "·" (ids in mono, level, people). */
  meta?: readonly ReactNode[] | undefined;
  /** The page's actions (Buttons; one red primary at most). */
  actions?: ReactNode | undefined;
  /** The 22 px title (a secondary page). */
  sm?: boolean | undefined;
  /** Loading: the title stays, the meta is skeleton blocks at its final geometry. */
  loading?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

export function PageHeader({ title, pills, meta, actions, sm, loading, id, className }: PageHeaderProps) {
  const head = useRef<HTMLDivElement | null>(null);
  const hasActions = actions !== undefined && actions !== null && actions !== false;
  const fit = usePageHeadFit(head, [title, meta?.length ?? 0, hasActions, Boolean(loading), Boolean(sm)]);
  return (
    <div
      ref={head}
      id={id}
      className={cx("mc-ph", hasActions && "mc-withacts", sm && "mc-sm", fit, className)}
      aria-busy={loading ? true : undefined}
    >
      <div className="mc-ph-pm">
        <div className="mc-ph-pt">
          <h1 className="mc-ph-h">{title}</h1>
          {loading ? null : pills}
        </div>
      </div>
      {loading ? (
        <div className="mc-ph-meta">
          <Skeleton width={96} height={14} />
          <Skeleton width={140} height={14} />
          <Skeleton width={120} height={14} />
        </div>
      ) : meta && meta.length > 0 ? (
        <div className="mc-ph-meta">
          {meta.map((fact, index) => (
            <Fragment key={index}>
              {index > 0 ? <span className="mc-ph-sep" aria-hidden="true">·</span> : null}
              <span>{fact}</span>
            </Fragment>
          ))}
        </div>
      ) : null}
      {hasActions ? <div className="mc-ph-acts">{actions}</div> : null}
    </div>
  );
}
